import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Lightning, ArrowRight, CircleNotch, CheckCircle } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (!token) { setErr("Missing reset token"); return; }
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white ll-grid-bg p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#ff8c00] flex items-center justify-center">
            <Lightning size={22} weight="fill" color="#000" />
          </div>
          <div>
            <div className="font-heading font-black text-xl tracking-tight leading-none">LOAD LOGIC</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mt-1">Reset Password</div>
          </div>
        </div>

        {done ? (
          <div className="border border-[#22c55e]/40 bg-[#22c55e]/10 p-6 text-center">
            <CheckCircle size={32} weight="fill" className="text-[#22c55e] mx-auto mb-3" />
            <div className="font-heading font-black text-lg mb-1">Password Updated</div>
            <div className="text-xs text-[#a1a1aa]">Redirecting to login…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">New Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-3 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                data-testid="new-password"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Confirm Password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-3 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                data-testid="confirm-password"
              />
            </label>
            {err && (
              <div className="border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] text-xs uppercase tracking-wider px-3 py-2.5" data-testid="reset-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-xs uppercase tracking-[0.25em] font-black transition-colors"
              data-testid="reset-submit"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <>Update Password <ArrowRight size={14} weight="bold" /></>}
            </button>
            <Link to="/login" className="block text-center text-[10px] uppercase tracking-[0.25em] text-[#71717a] hover:text-[#ff8c00] mt-4">
              Back to Login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
